import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import { Transaction } from '../types/budget';
import { formatCurrency } from '../utils/budgetCalculations';

interface DeleteTransactionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  transaction?: Transaction;
}

export const DeleteTransactionDialog: React.FC<DeleteTransactionDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  transaction,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center">
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />

        <div className="relative mx-auto w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
          <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
            {t('transaction.delete')}
          </Dialog.Title>

          <div className="mt-2">
            <p className="text-sm text-gray-500 dark:text-gray-300">{t('common.confirm')}</p>
            {transaction && (
              <div className="mt-3 rounded-md bg-gray-50 p-4 dark:bg-gray-700">
                <p className="mb-2 text-base font-medium text-gray-800 dark:text-gray-200">
                  {transaction.description}
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('transaction.category')}:
                    </span>{' '}
                    <span className="text-gray-700 dark:text-gray-300">{transaction.category}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('transaction.responsible')}:
                    </span>{' '}
                    <span className="text-gray-700 dark:text-gray-300">
                      {transaction.responsible}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('transaction.destination')}:
                    </span>{' '}
                    <span className="text-gray-700 dark:text-gray-300">
                      {transaction.destination}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">
                      {t('transaction.amount')}:
                    </span>{' '}
                    <span
                      className={`font-medium ${
                        transaction.amount >= 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {formatCurrency(transaction.amount)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end space-x-4">
            <button
              type="button"
              className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              onClick={onClose}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:bg-red-500 dark:hover:bg-red-600"
              onClick={() => {
                onConfirm();
                onClose();
              }}
            >
              {t('common.delete')}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
